import React from 'react'

import IndoorLegendGroupItem from 'pages/Device/ThermostatRthPanel/Legend/IndoorLegendGroupItem'
import LegendGroupSelection from 'pages/Device/ThermostatRthPanel/Legend/LegendGroupSection'

import styled, { css } from 'styled-components'

import { LegendItem, legendZone, legendDataForTimeAtCursor } from 'types/RuntimeHistory'

import _ from 'underscore'

import GA4 from 'helpers/ga4'

type Props = {
    legendItems: Required<Array<LegendItem>>
    zones: Array<legendZone>
    onIndoorLegendItemClickCallback: (seriesName: string, zoneName: string) => void
    onIndoorLegendSelectAllCallback:(seriesNames: Array<string>, isSelected: boolean) => void
    onIndoorZoneClickCallback: (series, zoneName) => void
    legendDataForTimeAtCursor: Required<legendDataForTimeAtCursor> | null
    selectedLegends: Array<{name: string, series: Array<string>}>
}

const IndoorLegendGroup = ({ legendItems, selectedLegends, zones, legendDataForTimeAtCursor, onIndoorZoneClickCallback, onIndoorLegendSelectAllCallback, onIndoorLegendItemClickCallback }: Props) => {
  const indoorLabels = _.pluck(legendItems, 'seriesName')

  const zoneSeriesWithData = (zoneName) => {
    const zoneSeries = _.find(zones, (zone) => zone.name === zoneName)?.zoneItems || []
    const zoneSeriesWithData = _.filter(zoneSeries, series => !series.emptyDataSet)
    return _.map(zoneSeriesWithData, (series) => series.seriesName)
  }

  // set true if all zone series with data selected
  const areAllSeriesSelected = () => {
    if (_.isEmpty(selectedLegends)) {
      return false
    }

    return _.all(selectedLegends, (selectedLegend) => {
      const seriesWithData = zoneSeriesWithData(selectedLegend.name)

      return _.without(seriesWithData, ...selectedLegend.series).length === 0
    })
  }

  // set true if all zone series with data not selected
  const areNoSeriesSelected = () => {
    if (_.isEmpty(selectedLegends)) {
      return true
    }

    return _.all(selectedLegends, (selectedLegend) => {
      const seriesWithData = zoneSeriesWithData(selectedLegend.name)

      return _.without(seriesWithData, ...selectedLegend.series).length === seriesWithData.length
    })
  }

  const onClickSelectAll = (selectAll) => {
    sendGA4event(selectAll ? 'Selected All metrics in a group' : 'Select No metrics in a group', 'Indoor')
    onIndoorLegendSelectAllCallback(indoorLabels, selectAll)
  }

  const onClickCheckmark = (zoneName, seriesName, isSelected) => {
    sendGA4event(isSelected ? 'Select a legend item for the graph' : 'Deselect a legend item for the graph', seriesName)
    onIndoorLegendItemClickCallback(seriesName, zoneName)
  }

  const onClickSeriesLabel = (seriesName, isSelected) => {
    sendGA4event(isSelected ? 'Select a legend item for the graph' : 'Deselect a legend item for the graph', seriesName)
    _.each(zones, zoneItem => {
      onIndoorLegendItemClickCallback(seriesName, zoneItem.name)
    })
  }

  const onClickZoneName = (isZoneSelected, zoneName) => {
    sendGA4event(isZoneSelected ? 'Select a legend item for the graph' : 'Deselect a legend item for the graph', zoneName)
    onIndoorZoneClickCallback(indoorLabels, zoneName)
  }

  const sendGA4event = (actionText, labelText) => {
    GA4.sendEvent({ category: 'Customer Detail Page', action: actionText, label: labelText })
  }

  return (
    <S.LegendGroup data-testid={'Indoor'}>
      <LegendGroupSelection areAllSeriesSelected={areAllSeriesSelected()} areNoSeriesSelected={areNoSeriesSelected()} onClickSelectAllCallback={onClickSelectAll} label={'Indoor'}>
        {zones.length > 1 &&
        (
        <S.ZoneHeaderContainer>
          <S.ZoneHeader>Zones</S.ZoneHeader>
          <S.ZoneNameContainer numberOfZones={zones.length}>
            {
            zones?.map(zone => {
              const isZoneSelected = !_.isEmpty(_.find(selectedLegends, item => item.name === zone.name)?.series)
              return <S.ZoneName key={zone.name} color={zone.color} isSelected={isZoneSelected} onClick={() => onClickZoneName(isZoneSelected, zone.name)}>{zone.name}</S.ZoneName>
            })
            }
          </S.ZoneNameContainer>
        </S.ZoneHeaderContainer>
        )}
        {legendItems.map((item) => {
          return (
            <IndoorLegendGroupItem
              onSelectCallback={onClickCheckmark}
              onSelectSeriesCallback={onClickSeriesLabel}
              legendItem={item}
              selectedLegendItems={selectedLegends}
              zones={zones}
              legendDataForTimeAtCursor={legendDataForTimeAtCursor}
              key={item.seriesName}
            />)
        })}
      </LegendGroupSelection>

      {legendDataForTimeAtCursor?.zones &&
        <>
          { zones.length > 1 && <S.AnscillaryContainer numberOfZones={zones.length}><S.AnscillaryDataLabel>Damper Position</S.AnscillaryDataLabel> <S.AnscillaryValueContainer numberOfZones={zones.length}>{legendDataForTimeAtCursor.zones.map(zone => <S.AnscillaryData key={zone.name}>{zone.damperPosition || '--'}</S.AnscillaryData>)}</S.AnscillaryValueContainer></S.AnscillaryContainer>}
          <S.AnscillaryContainer numberOfZones={zones.length}><S.AnscillaryDataLabel>Sys Status</S.AnscillaryDataLabel><S.AnscillaryValueContainer numberOfZones={zones.length}>{legendDataForTimeAtCursor.zones.map(zone => <S.AnscillaryData key={zone.name}>{zone.status || '--'}</S.AnscillaryData>)}</S.AnscillaryValueContainer></S.AnscillaryContainer>
          {legendDataForTimeAtCursor.zones[0]?.airflow && legendDataForTimeAtCursor.zones[0].airflow.length > 1 && <S.AnscillaryContainer numberOfZones={zones.length}><S.AnscillaryDataLabel>Airflow %</S.AnscillaryDataLabel><S.AnscillaryValueContainer numberOfZones={zones.length}>{legendDataForTimeAtCursor.zones.map(zone => <S.AnscillaryData key={zone.name}>{zone.airflow || '--'}</S.AnscillaryData>)}</S.AnscillaryValueContainer></S.AnscillaryContainer>}
          { zones.length > 1 && <S.AnscillaryContainer numberOfZones={zones.length}><S.AnscillaryDataLabel>Zone Mode</S.AnscillaryDataLabel> <S.AnscillaryValueContainer numberOfZones={zones.length}>{legendDataForTimeAtCursor.zones.map(zone => <S.AnscillaryData key={zone.name}>{zone.mode || '--'}</S.AnscillaryData>)}</S.AnscillaryValueContainer></S.AnscillaryContainer>}
        </>
     }
    </S.LegendGroup>
  )
}

export default IndoorLegendGroup

const S = {
  ZoneHeaderContainer: styled.div`
    display: flex;
  `,
  ZoneHeader: styled.div`
    font-weight: bold;
    text-align: right;
    margin-right: 10px;
    width: 150px;
    padding-right: 10px;
  `,
  ZoneNameContainer: styled.div.withConfig({
    shouldForwardProp: (prop) => !['numberOfZones'].includes(prop)
  })<{ numberOfZones: number }>`
    display: grid;
    grid-template-columns: ${({ numberOfZones }) => `repeat(${numberOfZones}, 80px);`};
    text-align: center;
    grid-column-gap: 10px;
    flex: 1;
  `,
  ZoneName: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isSelected', 'color'].includes(prop)
  })<{ isSelected:boolean, color: string }>`
    cursor: pointer;
    letter-spacing: .31px;
    ${({ color }) => css`color: ${color};`}
    ${({ isSelected }) => (isSelected
    ? css`font-weight: bold;`
    : css`font-weight: normal;`
  )}
  `,
  LegendGroup: styled.div`
    min-width: 250px;
    padding: 5px;
    margin: 5px;
  `,
  AnscillaryContainer: styled.div.withConfig({
    shouldForwardProp: (prop) => !['numberOfZones'].includes(prop)
  })<{ numberOfZones: number }>`
    display: flex;
    padding: 0px 13px 0px 13px;
  `,
  AnscillaryData: styled.div`
    display: flex;
    margin: auto;
    text-transform: capitalize;
  `,
  AnscillaryDataLabel: styled.div`
    font-weight: bold;
    padding-left: 32px;
    width: 150px;
    margin-right: 10px;
`,
  AnscillaryValueContainer: styled.div.withConfig({
    shouldForwardProp: (prop) => !['numberOfZones'].includes(prop)
  })<{ numberOfZones: number }>`
    display: grid;
    grid-template-columns: ${({ numberOfZones }) => `repeat(${numberOfZones}, 80px);`};
    grid-column-gap: 10px;
    text-align: center;
 `
}
